import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, Check, Smartphone, Monitor, Home, AlertTriangle, ArrowRight, Apple } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { TopLogoBar } from "@/components/AppShell";
import { InstallAnimation } from "@/components/InstallAnimation";
import { hasInstallPrompt, promptInstall, getPlatform, isSafari } from "@/lib/pwa";
import { PromoBanner } from "@/components/PromoBanner";

const fade = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.06, duration: 0.45 } }),
};

function Step({ n, title, desc, img }) {
  return (
    <div className="flex gap-4 rounded-2xl border border-[#27272A] bg-[#121215] p-4" data-testid={`install-step-${n}`}>
      <span className="tabular-nums font-semibold flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#E5C158] to-[#997A20] text-base font-bold text-[#09090B]">{n}</span>
      <div className="min-w-0 flex-1">
        <h3 className="text-lg text-[#F4F4F5] font-semibold">{title}</h3>
        <p className="mt-1 text-base leading-snug text-[#B4B4BC]">{desc}</p>
        {img && (
          <div className="mt-3 flex justify-center rounded-xl border border-[#27272A] bg-[#0D0D0F] p-2">
            <img src={img} alt="" className="h-24 w-24 object-contain" />
          </div>
        )}
      </div>
    </div>
  );
}

export default function InstallGuide() {
  const navigate = useNavigate();
  const detected = getPlatform();
  const isDesktop = detected === "other";
  const [device, setDevice] = useState(detected === "android" ? "android" : "ios");
  const [canPrompt, setCanPrompt] = useState(hasInstallPrompt());

  useEffect(() => {
    const on = () => setCanPrompt(true);
    window.addEventListener("a2g-installable", on);
    return () => window.removeEventListener("a2g-installable", on);
  }, []);

  const install = async () => { await promptInstall(); };
  const showSafariWarning = device === "ios" && detected === "ios" && !isSafari();

  const iosSteps = [
    { title: "Apri «Condividi»", desc: "Nella barra di Safari, in fondo allo schermo, tocca l'icona Condividi: il quadrato con la freccia verso l'alto ⬆. Se non la vedi, tocca prima «Aa» o i tre puntini «···» vicino all'indirizzo e poi «Condividi».", img: "/install/ios_share.jpg" },
    { title: "Scegli «Aggiungi alla schermata Home»", desc: "Nel menu che si apre, scorri verso il basso e tocca «Aggiungi alla schermata Home» (in inglese «Add to Home Screen»).", img: "/install/ios_addhome.jpg" },
    { title: "Conferma e hai finito", desc: "In alto a destra tocca «Aggiungi». L'icona di Coach2Go comparirà tra le tue app, pronta all'uso. Ricorda: su iPhone funziona solo con Safari." },
  ];
  const androidSteps = [
    { title: "Apri il menu del browser", desc: "In alto a destra (in Chrome) tocca i tre puntini in colonna «⋮».", img: "/install/android_menu.jpg" },
    { title: "Tocca «Installa app»", desc: "Nel menu scegli «Installa app». Se non c'è, tocca «Aggiungi a schermata Home».", img: "/install/android_install.jpg" },
    { title: "Conferma e hai finito", desc: "Conferma l'installazione: l'icona di Coach2Go comparirà tra le tue app." },
  ];

  const steps = device === "ios" ? iosSteps : androidSteps;

  return (
    <div className="min-h-screen px-5 pb-16 pt-6" data-testid="install-guide-page">
      <TopLogoBar onBack={() => navigate(-1)} testid="install-back-button" />
      <div className="-mx-5 mb-4"><PromoBanner /></div>

      <motion.div variants={fade} custom={0} initial="hidden" animate="show" className="mt-2">
        <p className="eyebrow">Passo 1 · Installa l'app</p>
        <h1 className="font-serif-el mt-2 text-3xl font-semibold leading-tight text-[#FAF9F6]">Metti Coach2Go tra le tue app</h1>
        <p className="mt-3 text-base leading-relaxed text-[#B4B4BC]">
          Bastano pochi tocchi. Alla fine avrai l'icona di Coach2Go sul telefono, proprio come qualsiasi altra app, senza passare dallo store.
        </p>
        <div className="mt-4 flex gap-3 rounded-2xl border border-[#27272A] bg-[#121215] p-4">
          <Home size={18} className="mt-0.5 shrink-0 text-[#D4AF37]" />
          <p className="text-sm leading-snug text-[#B4B4BC]">
            <b className="text-[#D4D4D8]">La “schermata Home”</b> è la pagina del telefono dove trovi tutte le tue app: è lì che aggiungeremo Coach2Go.
          </p>
        </div>
      </motion.div>

      {isDesktop && (
        <motion.section variants={fade} custom={1} initial="hidden" animate="show" className="mt-6" data-testid="desktop-install-section">
          <div className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#18181C] to-[#0D0D0F] p-5">
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <Monitor size={16} />
              <span className="text-sm font-semibold uppercase tracking-[0.2em]">Sei su computer?</span>
            </div>
            <p className="mt-3 text-base leading-relaxed text-[#B4B4BC]">
              Coach2Go dà il meglio sul telefono. Inquadra questo codice QR con la fotocamera dello smartphone per aprirla e installarla lì.
            </p>
            <div className="mt-4 flex justify-center">
              <div className="rounded-2xl bg-white p-3">
                <QRCodeCanvas value={typeof window !== "undefined" ? window.location.origin : ""} size={168} bgColor="#ffffff" fgColor="#0A0A0C" level="M" includeMargin={false} data-testid="install-qr" />
              </div>
            </div>
            <div className="mt-5 space-y-3">
              {canPrompt && (
                <button onClick={install} data-testid="desktop-install-button" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base uppercase tracking-wide">
                  <Download size={18} /> Installa sul computer
                </button>
              )}
              <button onClick={() => navigate("/test")} data-testid="desktop-continue-button" className="btn-outline-gold w-full rounded-full px-6 py-3.5 text-base">
                Continua nel browser
              </button>
            </div>
          </div>
          <p className="mt-6 text-center text-sm text-[#8A8E96]">Oppure segui i passaggi per il telefono qui sotto 👇</p>
        </motion.section>
      )}

      {/* Selettore dispositivo */}
      <motion.div variants={fade} custom={2} initial="hidden" animate="show" className="mt-7">
        <div className="grid grid-cols-2 gap-2 rounded-2xl border border-[#27272A] bg-[#121215] p-1.5">
          <button
            onClick={() => setDevice("ios")}
            data-testid="device-ios"
            className={`flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${device ==="ios" ? "bg-[#D4AF37] text-[#09090B]" : "text-[#B4B4BC]"}`}
          >
            <Apple size={15} /> iPhone / iPad
          </button>
          <button
            onClick={() => setDevice("android")}
            data-testid="device-android"
            className={`flex items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${device ==="android" ? "bg-[#D4AF37] text-[#09090B]" : "text-[#B4B4BC]"}`}
          >
            <Smartphone size={15} /> Android / altro
          </button>
        </div>
        <p className="mt-2 text-center text-sm text-[#8A8E96]">
          {device === "ios" ? "Non hai un iPhone? " : "Hai un iPhone? "}
          <button onClick={() => setDevice(device === "ios" ? "android" : "ios")} data-testid="device-switch" className="font-semibold text-[#D4AF37] underline">
            {device === "ios" ? "Passa ad Android" : "Passa a iPhone"}
          </button>
        </p>
      </motion.div>

      {showSafariWarning && (
        <motion.div variants={fade} custom={3} initial="hidden" animate="show" className="mt-5 flex gap-3 rounded-2xl border border-[#C85A32]/40 bg-[#C85A32]/12 p-4" data-testid="safari-warning">
          <AlertTriangle size={18} className="mt-0.5 shrink-0 text-[#E08A5B]" />
          <p className="text-sm leading-snug text-[#E8C9B8]">
            Su iPhone l'installazione funziona solo con <b>Safari</b>. Se hai aperto questa pagina da un altro browser o da un'app (es. Instagram), tocca i tre puntini e scegli <b>“Apri in Safari”</b>, poi segui i passaggi.
          </p>
        </motion.div>
      )}

      {/* Passaggi del dispositivo selezionato */}
      <motion.section key={device} variants={fade} custom={4} initial="hidden" animate="show" className="mt-6" data-testid={`steps-${device}`}>
        <div className="mb-4 flex items-center gap-2 text-[#D4AF37]">
          <Smartphone size={16} />
          <span className="text-sm font-semibold uppercase tracking-[0.2em]">{device === "ios" ? "Su iPhone / iPad" : "Su Android"}</span>
        </div>

        {/* Mini clip animata dei passaggi */}
        <div className="mb-5 rounded-3xl border border-[#D4AF37]/20 bg-gradient-to-br from-[#141416] to-[#0A0A0C] p-5" data-testid="install-animation-card">
          <p className="mb-4 text-center text-sm text-[#8A8E96]">Guarda come si fa, passo dopo passo 👇</p>
          <InstallAnimation device={device} />
        </div>

        <div className="space-y-3">
          {steps.map((s, i) => (
            <Step key={i} n={i + 1} title={s.title} desc={s.desc} img={s.img} />
          ))}
        </div>
        {device === "android" && canPrompt && (
          <button onClick={install} data-testid="android-install-button" className="btn-gold mt-4 flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-base uppercase tracking-wide">
            <Download size={18} /> Installa l'app ora
          </button>
        )}
      </motion.section>

      {/* Passo 2 */}
      <motion.section variants={fade} custom={5} initial="hidden" animate="show" className="mt-10">
        <div className="rounded-3xl border border-[#D4AF37]/25 bg-gradient-to-br from-[#18181C] to-[#0D0D0F] p-5 text-center">
          <p className="eyebrow">Passo 2 · Inizia</p>
          <h2 className="font-serif-el mt-2 text-2xl font-semibold text-[#FAF9F6]">Hai installato l'app?</h2>
          <p className="mt-2 text-base leading-relaxed text-[#B4B4BC]">
            Aprila dalla schermata Home toccando la sua icona: partirà subito il breve test per scoprire il tuo archetipo.
          </p>
          <button onClick={() => navigate("/test")} data-testid="install-cta-test" className="btn-gold mt-5 flex w-full items-center justify-center gap-2 rounded-full px-6 py-4 text-base uppercase tracking-wide">
            Fai il test ora <ArrowRight size={18} />
          </button>
          <button onClick={() => navigate("/")} data-testid="install-cta-home" className="mt-3 w-full text-center text-sm font-medium text-[#8A8E96]">
            Torna alla home
          </button>
        </div>
      </motion.section>
    </div>
  );
}
